import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, TextField, Grid, Divider, Alert,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DownloadIcon from '@mui/icons-material/Download';

/**
 * 거래명세표 출력 전에 양식에서 비는 칸을 받는다.
 *
 * 원래는 window.prompt 를 두 번 띄웠다. 브라우저 기본 팝업이라 화면을 가리고,
 * 앞 항목을 잘못 넣어도 되돌아갈 수 없고, 무엇을 위해 묻는지도 안 보였다.
 * 한 번에 받고, 무엇이 이미 채워져 있는지 함께 보여준다.
 *
 * 여기 있는 항목들은 시스템에 둘 자리가 없어서 그때그때 받는 값이다.
 *   대표자명 · 업태 · 종목 : 거래처 정보에 항목 자체가 없다
 *   인수자                : 물건을 받는 사람이라 출고 건마다 다르다
 *   비고 · 메모            : 매번 다르다
 */
const ShipmentStatementDialog = ({ open, data, loading, onClose, onSubmit }) => {
  const [values, setValues] = useState({
    buyerCeo: '',
    receiver: '',
    remark: '',
    memo: '',
  });

  useEffect(() => {
    if (open) {
      setValues({ buyerCeo: '', receiver: '', remark: '', memo: '' });
    }
  }, [open, data]);

  const change = (field) => (e) =>
    setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const buyerNm = data?.buyer?.partnerNm || '';
  const items = data?.items || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h6">거래명세표 출력</Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* 이미 채워진 값은 다시 묻지 않는다. 무엇이 나갈지만 보여준다. */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">출고번호</Typography>
              <Typography variant="body2">{data?.shipmentNumber || '-'}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">출고일</Typography>
              <Typography variant="body2">{data?.shipmentDate || '-'}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">품목</Typography>
              <Typography variant="body2">{items.length}건</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">공급받는자</Typography>
              <Typography variant="body2">{buyerNm || '-'}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            아래 항목은 시스템에 저장된 자리가 없어 출력할 때마다 받습니다.
            비워두면 그 칸은 빈칸으로 인쇄됩니다.
          </Typography>
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="대표자명"
              value={values.buyerCeo}
              onChange={change('buyerCeo')}
              placeholder={buyerNm ? `${buyerNm} 대표자` : '공급받는자 대표자'}
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="인수자"
              value={values.receiver}
              onChange={change('receiver')}
              placeholder="물건을 받는 분"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="비고"
              value={values.remark}
              onChange={change('remark')}
              placeholder="공급받는자 칸 옆에 인쇄됩니다"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="메모"
              value={values.memo}
              onChange={change('memo')}
              placeholder="표 아래에 인쇄됩니다"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          취소
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => onSubmit(values)}
          disabled={loading}
        >
          {loading ? '만드는 중...' : '출력'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShipmentStatementDialog;
